import { createContext, ReactNode, useEffect, useMemo, useState } from 'react'
import { getAuth, User, onAuthStateChanged } from 'firebase/auth'
import { initializeApp } from 'firebase/app'
import { PermissionEnum, UserPermissions } from 'api/userPermissions'
import { useUserPermissions } from 'queries/user'
import { Loading } from 'ui'
import {
  CompaniesResponse,
  CompanyInfo,
  getCompanies,
  getCurrentCompanyInfo,
} from 'api/company'
import { isDemoMode, reactivateDemoMode } from 'api/demo'
import { config } from 'config'
import { useSyncTelemetryUser } from '@workwhile/telemetry/react'
import { useAuthContext } from './useAuthContext'
import {
  setAmplitudeUserId,
  useAmplitudeIdentify,
  reset,
  identify,
} from 'lib/amplitude'
import { IIdentify } from '@amplitude/analytics-browser/lib/esm/types'
import { useQuery } from '@tanstack/react-query'
import { companiesKey, companyInfoKey } from 'queries/company/keys'
import { STORED_COMPANY_ID_KEY } from '../../constants'

const TelemetryUserSync = () => {
  const { user, company } = useAuthContext()

  const telemetryUser = useMemo(() => {
    return {
      id: user?.uid,
      email: user?.email || undefined,
      companyId: company?.id,
    }
  }, [user, company])

  useSyncTelemetryUser(telemetryUser)

  return null
}

const app = initializeApp(config.firebaseConfig)
export const auth = getAuth(app)

export interface AuthContextValueProps {
  user: User | null
  company: CompanyInfo | null
  isAuthenticated: boolean
  companies: CompaniesResponse['companies']
  permissions: UserPermissions | null
  hasPermission: (permission: PermissionEnum) => boolean
  identity: IIdentify
  companyChangedInAnotherTab: boolean
}

export const AuthContext = createContext<AuthContextValueProps>(null!)

interface Props {
  children: ReactNode
}

export const AuthProvider = (props: Props) => {
  const [initializing, setInitializing] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [companyChangedInAnotherTab, setCompanyChangedInAnotherTab] =
    useState(false)
  const identity = useAmplitudeIdentify()

  // We don't want to surface this endpoint via queries
  // because we want other components to use useAuthContext() for this data
  const { data: companyInfo, isFetching: isLoadingCompanyInfo } = useQuery({
    queryKey: [user?.uid, ...companyInfoKey],
    queryFn: getCurrentCompanyInfo,
    enabled: !!user,
  })

  // We don't want to surface this endpoint via queries
  // because we want other components to use useAuthContext() for this data
  const { data: companies, isFetching: isLoadingCompanies } = useQuery({
    queryKey: [user?.uid, ...companiesKey],
    queryFn: () => getCompanies(),
    enabled: !!user,
  })

  const { data: permissions, isFetching: isLoadingPermissions } =
    useUserPermissions(user?.uid)

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      setUser(user)
      if (user?.uid) {
        setAmplitudeUserId(user.uid)
      } else {
        reset()
      }
      if (initializing) {
        setInitializing(false)
      }
    })
  }, [])

  useEffect(() => {
    const isDemo = isDemoMode()
    if (isDemo) {
      reactivateDemoMode()
    }
  }, [])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== STORED_COMPANY_ID_KEY) {
        return
      }

      const newCompanyId = event.newValue
      if (newCompanyId && newCompanyId !== companyInfo?.id?.toString()) {
        setCompanyChangedInAnotherTab(true)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [companyInfo])

  useEffect(() => {
    if (companyInfo?.id) {
      localStorage.setItem(STORED_COMPANY_ID_KEY, companyInfo.id.toString())
    }
  }, [companyInfo])

  useEffect(() => {
    if (!user) {
      return
    }

    if (companyInfo) {
      identity.set('company_id', companyInfo.id)
    } else {
      identity.unset('company_id')
    }

    setAmplitudeUserId(user.uid)
    identity.set('user_id', user.uid)
    identify(identity)
  }, [user, companyInfo, identity])

  const isLoadingAuthData =
    !!user &&
    (isLoadingCompanyInfo || isLoadingCompanies || isLoadingPermissions)

  const values = useMemo(() => {
    if (initializing || isLoadingAuthData) {
      return null
    }

    return {
      user,
      company: companyInfo || null,
      companies: companies || [],
      permissions: permissions || null,
      isAuthenticated: !!user,
      hasPermission: (permission: PermissionEnum) => {
        if (!permissions) {
          return false
        }
        return permissions[permission]
      },
      identity,
      companyChangedInAnotherTab,
    } satisfies AuthContextValueProps
  }, [
    user,
    permissions,
    companyInfo,
    companies,
    identity,
    initializing,
    isLoadingAuthData,
    companyChangedInAnotherTab,
  ])

  if (initializing || isLoadingAuthData || !values) {
    return <Loading />
  }

  return (
    <AuthContext.Provider value={values}>
      <TelemetryUserSync />
      {props.children}
    </AuthContext.Provider>
  )
}
